import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { MultiConfirmationModal } from "../../../../components/BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { getMeterNameWithNumber } from "../../../../components/ComponentListContainer/ComponentList/columns/getMeterCustomColumns";
import { LoadOrError } from "../../../../components/LoadOrError/LoadOrError";
import { getDefaultMRTOptions } from "../../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { useToast } from "../../../../mantine/notifications/useToast";
import type { MeteringOrMarketLocation } from "../../../../utils/backend-types";
import { useParagraph6GemeindeAnteilErzeugerMutations } from "../../hooks/useParagraph6GemeindeAnteilErzeugerMutations";
import { useParagraph6UnassignedGenerators } from "../../hooks/useParagraph6UnassignedGenerators";
import type {
  Paragraph6ContractResponse,
  Paragraph6GemeindeAnteilErzeugerResponse
} from "../../Paragraph6.types";
import { getGeneratorColumn } from "./columns/getGeneratorColumn";
import { getWeightingColumn } from "./columns/getWeightingColumn";
import "./Paragraph6ContractInformation.scss";

const DEFAULT_SORTED = [
  {
    id: "generator_display_name",
    desc: false
  }
];

type Paragraph6ContractInformationProps = {
  contract: Paragraph6ContractResponse;
  meteringOrMarketLocations: Array<MeteringOrMarketLocation>;
  onClickNewGeneratorShare: (contractId: string) => void;
  onClickEditGeneratorShare: (generatorShareId: number) => void;
};

function Paragraph6ContractInformation({
  contract,
  meteringOrMarketLocations,
  onClickNewGeneratorShare,
  onClickEditGeneratorShare
}: Paragraph6ContractInformationProps) {
  const { showToast, showErrorToast } = useToast();

  const generatorShares = contract.gemeinde_anteile_erzeuger || [];

  const [sharesForDeletion, setSharesForDeletion] = useState<
    Array<Paragraph6GemeindeAnteilErzeugerResponse>
  >([]);

  const getNamesOfGeneratorSharesToDelete = (
    generatorShares: Array<Paragraph6GemeindeAnteilErzeugerResponse>
  ) => {
    return generatorShares
      .map((generatorShare) => generatorShare.generator_display_name)
      .filter((generatorName) => generatorName !== undefined);
  };

  const {
    paragraph6UnassignedGenerators,
    isLoading: paragraph6UnassignedGeneratorsLoading
  } = useParagraph6UnassignedGenerators(contract.id);

  const { paragraph6GemeindeAnteilErzeugerDeleteMutation } =
    useParagraph6GemeindeAnteilErzeugerMutations(contract.variant, contract.id);

  function deleteGeneratorShares() {
    const deletePromises = sharesForDeletion.map((share) =>
      paragraph6GemeindeAnteilErzeugerDeleteMutation.mutateAsync(
        share.generator
      )
    );
    return Promise.all(deletePromises)
      .then(() => {
        table.setRowSelection({});
        setSharesForDeletion([]);
        showToast({
          type: "success",
          message: `Erzeuger „${getNamesOfGeneratorSharesToDelete(
            sharesForDeletion
          ).join(", ")}“ wurde erfolgreich aus dem Vertrag entfernt.`,
          autoClose: false,
          withCloseButton: true
        });
      })
      .catch((error) => {
        showErrorToast(error);
      });
  }

  const columns: Array<
    MRT_ColumnDef<Paragraph6GemeindeAnteilErzeugerResponse>
  > = useMemo(
    () => [
      getGeneratorColumn(),
      {
        id: "metering_or_market_location",
        header: "Einspeisezeitreihe: Zählpunkt",
        accessorFn: ({ metering_or_market_location }) =>
          getMeterNameWithNumber({
            rowLocation: metering_or_market_location,
            allLocations: meteringOrMarketLocations
          })
      },
      getWeightingColumn({
        key: "weighting"
      }),
      {
        header: "Anteil Gemeinde",
        accessorKey: "community_share"
      }
    ],
    [meteringOrMarketLocations]
  );

  const table = useMantineReactTable<Paragraph6GemeindeAnteilErzeugerResponse>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Es gibt noch keine Erzeuger",
      initialState: { sorting: DEFAULT_SORTED },
      hideBorder: true
    }),
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Erzeuger hinzufügen",
        disabled:
          !paragraph6UnassignedGenerators ||
          paragraph6UnassignedGenerators.length === 0,
        variant: "outline",
        onClick: () => onClickNewGeneratorShare(contract.id)
      },
      delete: {
        text: "Erzeuger entfernen",
        onClick: (rowSelection) => {
          const selectedShares = Object.keys(rowSelection).map(
            (key) => table.getRow(key).original
          );
          setSharesForDeletion(selectedShares);
        }
      }
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        edit: {
          text: "Erzeuger bearbeiten",
          onClick: (row) => {
            onClickEditGeneratorShare(row.original.generator);
            table.setEditingRow(null);
          }
        },
        delete: {
          text: "Erzeuger aus dem Vertrag entfernen?",
          onClick: (row) => setSharesForDeletion([row.original])
        }
      }
    }),
    enablePagination: false,
    enableToolbarInternalActions: false,
    columns: columns,
    data: generatorShares
  });

  return (
    <>
      <LoadOrError
        loading={paragraph6UnassignedGeneratorsLoading}
        loadingMessage="Vertragsinformationen werden geladen ..."
      >
        <MantineReactTable table={table} />
      </LoadOrError>
      <MultiConfirmationModal
        actionName="aus dem Vertrag entfernen"
        actionObjects={getNamesOfGeneratorSharesToDelete(sharesForDeletion)}
        confirmationText="Dieser Schritt kann nicht rückgängig gemacht werden."
        headerText="Wollen Sie die folgenden Erzeuger aus dem Vertrag entfernen?"
        isModalOpen={sharesForDeletion.length > 0}
        objectName="Erzeuger"
        toggleModal={() => setSharesForDeletion([])}
        onAction={deleteGeneratorShares}
      />
    </>
  );
}

export { Paragraph6ContractInformation, Paragraph6ContractInformationProps };
